import { atom } from "jotai";
import {
  activeProfileAtom,
  activeStudioProfileAtom,
} from "../../../atoms/user/activeProfileAtom";
import {
  activeUserAtom,
  activeUserDisciplinesAtom,
} from "../../../atoms/user/activeUserAtom";
import { activeUserStudiosAtom } from "../../../atoms/user/activeUserStudiosAtom";
import { DISCIPLINE_TYPE } from "../../../hooks/user";
import { parseUrlQueryParams } from "../../../utils/searchFiltersUtils";

export const enum UserDashboardTypeOptions {
  None = "#none",
  Artist = "#artist",
  Engineer = "#engineer",
}

export const NON_STUDIO_DASHBOARD_TYPES: string[] = [
  UserDashboardTypeOptions.Engineer,
  UserDashboardTypeOptions.Artist,
];

export enum DashboardScreenParamKeys {
  DashboardType = "dashboard_type",
}

export const availableDashboardTypesAtom = atom((get) => {
  const activeUser = get(activeUserAtom);
  const activeStudios = get(activeUserStudiosAtom);
  const userOptions = [];
  const studioOptions = [];
  if (activeUser?.disciplines?.artist?.id) {
    userOptions.push({
      value: UserDashboardTypeOptions.Artist,
      label: "Artist",
    });
  }

  if (activeUser?.disciplines?.engineer?.id) {
    userOptions.push({
      value: UserDashboardTypeOptions.Engineer,
      label: "Engineer",
    });
  }

  for (const studio of activeStudios) {
    studioOptions.push({
      value: `${studio.username}`,
      label: `${studio.displayableName} `,
    });
  }

  return {
    userOptions,
    studioOptions,
    availableOptions: [...userOptions, ...studioOptions],
  };
});

export const activeDashboardAtom = atom<string>();

export const activeDashboardDerivedAtom = atom(
  (get) => {
    const activeDashboard = get(activeDashboardAtom);
    const activeUserDisciplines = get(activeUserDisciplinesAtom);
    const userStudios = get(activeUserStudiosAtom);
    const activeUser = get(activeUserAtom);
    const activeStudio = get(activeStudioProfileAtom);

    // The dashboard type coming from the URL should take precedence
    const parsedQueryObject = parseUrlQueryParams();
    const parsedActiveDashboard =
      parsedQueryObject[DashboardScreenParamKeys.DashboardType];
    if (
      parsedActiveDashboard === UserDashboardTypeOptions.Artist &&
      activeUserDisciplines.artist
    ) {
      return UserDashboardTypeOptions.Artist;
    }

    if (
      parsedActiveDashboard === UserDashboardTypeOptions.Engineer &&
      activeUserDisciplines.engineer
    ) {
      return UserDashboardTypeOptions.Engineer;
    }

    if (parsedActiveDashboard) {
      const parsedActiveDashboardStudio = userStudios.find(
        (studio) => studio.username === parsedActiveDashboard,
      );
      if (parsedActiveDashboardStudio) {
        return parsedActiveDashboardStudio.username;
      }
    }

    // If there is no dashboard type in the URL, check if there is an active studio from the profile dropdown
    // It should take precedence next
    if (activeStudio) {
      return activeStudio.username;
    }

    // If a non-studio active dashboard atom has been set previously, use it!
    if (
      activeDashboard &&
      NON_STUDIO_DASHBOARD_TYPES.includes(activeDashboard)
    ) {
      return activeDashboard;
    }

    // Otherwise, use the user's primary discipline
    if (activeUser?.primaryDiscipline === DISCIPLINE_TYPE.ARTIST) {
      return UserDashboardTypeOptions.Artist;
    }

    if (activeUser?.primaryDiscipline === DISCIPLINE_TYPE.ENGINEER) {
      return UserDashboardTypeOptions.Engineer;
    }

    return undefined;
  },
  (get, set, updatedDashboard: string) => {
    if (updatedDashboard) {
      const activeUserDisciplines = get(activeUserDisciplinesAtom);
      if (
        (updatedDashboard === UserDashboardTypeOptions.Artist &&
          activeUserDisciplines.artist) ||
        (updatedDashboard === UserDashboardTypeOptions.Engineer &&
          activeUserDisciplines.engineer)
      ) {
        set(activeProfileAtom, "0");
        set(activeDashboardAtom, updatedDashboard);
        return;
      }

      const userStudios = get(activeUserStudiosAtom);
      const foundStudio = userStudios.find(
        (studio) => studio.username === updatedDashboard,
      );

      if (foundStudio) {
        set(activeDashboardAtom, updatedDashboard);
        set(activeProfileAtom, foundStudio.id.toString());
      }
    }
  },
);

// This derived atom will determine its value from the selected dashboard type.
// It will return an object in the form of {accounttypeId: value}.
export const activeDashboardValueAtom = atom((get) => {
  const activeDashboard = get(activeDashboardDerivedAtom);
  const activeUserDisciplines = get(activeUserDisciplinesAtom);
  const activeProfile = get(activeProfileAtom);
  const activeStudio = get(activeStudioProfileAtom);

  if (!activeProfile || !activeUserDisciplines || !activeDashboard) return {};

  if (
    activeDashboard === UserDashboardTypeOptions.Artist &&
    activeUserDisciplines.artist
  ) {
    return {
      artist_id: activeUserDisciplines.artist.id,
      user_id: activeProfile.id,
    };
  }

  if (
    activeDashboard === UserDashboardTypeOptions.Engineer &&
    activeUserDisciplines.engineer
  ) {
    return {
      engineer_id: activeUserDisciplines.engineer.id,
      user_id: activeProfile?.id,
    };
  }

  if (!activeStudio) return {};

  return {
    studio_id: activeStudio.id,
    user_id: activeProfile.id,
  };
});

export const activeDashboardBooleanAtoms = atom((get) => {
  const activeDashboardValue = get(activeDashboardValueAtom);

  return {
    isArtistSelected: Boolean(activeDashboardValue.artist_id),
    isEngineerSelected: Boolean(activeDashboardValue.engineer_id),
    isStudioSelected: Boolean(activeDashboardValue.studio_id),
  };
});
